import React from "react";
import apiService from "../../../services/ApiService";
import TailwindSelect from "../../Tailwind/TailwindSelect";
import snakeToPascal from "../../../tools";
import { useSaveIndicator } from "../../../hooks/useSaveIndicator";
import SaveIndicator from "../../SaveIndicator";
import { useEditorStore } from "../../../shared/stores/useEditorStore";
import { useQueryClient } from "@tanstack/react-query";
import { SECTION_DATA_KEY } from "../../../shared/hooks/queries/useSectionLoader";


export default function SensitivityRelationField({ name, item, field, currentSensitivity }) {
    const isReadOnly = !useEditorStore(s => s.userRights.right_to_write);
    const scenarioId = useEditorStore(s => s.scenarioId) ?? 0;
    const queryClient = useQueryClient();

    const invalidateQueries = () => {
        // Only invalidate section-data cache — do NOT invalidate SCENARIO_TREE_KEY (see useUpdateFieldValue.ts)
        queryClient.invalidateQueries({ queryKey: [SECTION_DATA_KEY, scenarioId] });
    };

    const sensitivityField = item.sensitivityModelData.fields[field.name];

    const safeOriginalValue = field.currentValue?.value ?? 'null';
    const originalValue = safeOriginalValue !== 'null' ? safeOriginalValue.toString() : '';
    const safeSensitivityValue = sensitivityField?.value?.value ?? 'null';
    const initialValue = sensitivityField ? (safeSensitivityValue !== 'null' ? safeSensitivityValue.toString() : '') : originalValue;

    const [fieldValue, setFieldValue] = React.useState(initialValue);
    const { status: saveStatus, trigger: triggerSave, resolve: resolveSave } = useSaveIndicator();
    const [options, setOptions] = React.useState([]);
    const [optionsLoading, setOptionsLoading] = React.useState(true);

    React.useEffect(() => {
        setOptionsLoading(true);
        apiService.getRelationOptions(field.id).then(async (response) => {
            if (response) {
                const data = await response.json();
                setOptions(data.map((relation: any) => ({
                    value: relation.id.toString(),
                    label: relation.name,
                })));
            }
            setOptionsLoading(false);
        });
    }, [field.id]);

    const handleChange = (newValueId: string) => {
        const apiValue = (newValueId && newValueId !== 'null' && newValueId !== '') ? parseInt(newValueId) : null;

        setFieldValue(newValueId);
        triggerSave();
        apiService.upsertSensitivityValue(item.sensitivityModelData.id, field.name, field.type, apiValue).then(
            async(response) => {
                if (response) {
                    const updatedField = await response.json()
                    item.sensitivityModelData.fields[field.name] = updatedField
                    resolveSave();
                    invalidateQueries();
                }
            }
        )
    }

    const resetSensitivityValue = () => {
        apiService.deleteSensitivityValue(sensitivityField.id).then(
            async(response) => {
                if (response) {
                    delete item.sensitivityModelData.fields[field.name];
                    setFieldValue(originalValue);
                    invalidateQueries();
                }
            }
        )
    }

    return (
        <div className="flex flex-row items-center">
            {!isReadOnly && (
                <button
                    onClick={resetSensitivityValue}
                    disabled={!sensitivityField}
                    className="mr-2 h-10 w-10 flex items-center justify-center text-slate-600 hover:text-primary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                    <span className="material-symbols-outlined text-[30px]">restart_alt</span>
                </button>
            )}
            <div className="w-[200px]">
                <TailwindSelect
                    value={fieldValue}
                    onChange={handleChange}
                    options={options}
                    placeholder={snakeToPascal(name)}
                    disabled={isReadOnly || optionsLoading}
                />
            </div>
            <SaveIndicator status={saveStatus} className="ml-2" />
        </div>
    )
}
